import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { darkModeLightActive } from 'src/components/Reports/Reports.css';
import { ReportsInventoryExport } from 'src/components/Reports/ReportsExport';
import {
  ReportExportOptions,
  ReportExportOptionsV2,
} from 'src/components/Reports/ReportsExport/ReportsExport.types';
import { ReportsExportV2 } from 'src/components/Reports/ReportsExport/ReportsExportV2';
import { ReportsSaleExport } from 'src/components/Reports/ReportsExport/ReportsSaleExport';
import { Content } from 'src/contexts/ContentContext';
import { useFileExportContext } from 'src/contexts/FileExportContext';
import { vars } from 'src/core/themes';
import { DropdownMenu, Stack } from 'src/core/ui';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { ReportConfigV2 } from 'src/hooks/reportConfigs';
import { ReportConfig } from 'src/hooks/useReportConfigs';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { CopyIcon } from 'src/svgs';
import { OpenLinkIcon } from 'src/svgs/OpenLinkIcon';
import {
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  GenericDisclosuresIcon,
  HistoryIcon,
  IconsFill,
  MoreIcon,
  ProcessingIcon,
  ShareIcon,
} from 'src/svgs/Viagogo';
import { DataLagIndicator } from 'src/tables/ReportTableCommon/DataLagIndicator';
import { TableShimmeringDiv } from 'src/tables/Table/Table.styled';
import { ContentId } from 'src/utils/constants/contentId';
import { ReportTypes, ReportTypesV2 } from 'src/utils/reportsUtils';
import { GridActionType } from 'src/utils/tableUtils';
import {
  Feature,
  Permission,
  ReportFileType,
  ReportGroupBy,
} from 'src/WebApiController';

export type ReportWithHandlers = {
  report?: ReportConfig;
  reportV2?: ReportConfigV2;
  onReportAction?: (
    report: ReportConfig | undefined,
    reportV2: ReportConfigV2 | undefined,
    actionType: GridActionType,
    event?: React.MouseEvent
  ) => void;
};

export const REPORTS_TABLE_COLUMNS_CONFIG: ColumnDef<ReportWithHandlers | null>[] =
  [
    {
      id: 'reportName',
      header: () => (
        <TableHeaderCellDiv align="left">
          <Content id={ContentId.Name} />
        </TableHeaderCellDiv>
      ),
      minSize: 50,
      maxSize: 200,
      accessorFn: (data) =>
        data?.report?.reportName ?? data?.reportV2?.reportName,
      cell: ({ getValue, row: { original } }) => {
        const value = getValue() as string;
        if (!original) {
          return <TableShimmeringDiv />;
        }
        return (
          <TableCellDiv
            style={{
              textTransform: 'unset',
              gap: vars.spacing['sm'],
              color: vars.color.textStrong,
              fontWeight: 500,
            }}
            align="left"
          >
            <GenericDisclosuresIcon fill={vars.color.textStrong} />
            <span>{value}</span>
          </TableCellDiv>
        );
      },
    },
    {
      id: 'ownerDisplayName',
      header: () => (
        <TableHeaderCellDiv align="left">
          <Content id={ContentId.Owner} />
        </TableHeaderCellDiv>
      ),
      accessorFn: (data) =>
        data?.report?.ownerDisplayName ?? data?.reportV2?.ownerDisplayName,
      cell: ({ getValue, row: { original } }) => {
        const value = getValue() as string | null | undefined;
        if (!original) {
          return <TableShimmeringDiv />;
        }
        return (
          <TableCellDiv
            align="left"
            style={{ color: vars.color.textStrong, fontWeight: 500 }}
          >
            <>
              {(original.report?.globalReportTypeId ??
                original.reportV2?.globalReportTypeId) != null ? (
                <Content id={ContentId.Default} />
              ) : (
                <>{value}</>
              )}
            </>
          </TableCellDiv>
        );
      },
    },
    {
      id: 'actions',
      enableSorting: false,
      header: () => (
        <TableHeaderCellDiv align="right">
          <Content id={ContentId.Actions} />
        </TableHeaderCellDiv>
      ),
      minSize: 50,
      maxSize: 50,
      cell: function Cell({ row: { original } }) {
        const canCreateReport = useUserHasAnyOfPermissions(
          Permission.Reports_CreateReports
        );
        const canUpdateReport = useUserHasAnyOfPermissions(
          Permission.Reports_UpdateReports
        );
        const canUpdateOwnReports = useUserHasAnyOfPermissions(
          Permission.Reports_UpdateReportsOwned
        );
        const canViewHistory = useUserHasFeature(Feature.ReportSnapshot);
        const hasExportInventoryFeature = useUserHasFeature(
          Feature.ExportInventory
        );

        const [reportExportOptions, setReportExportOptions] =
          useState<ReportExportOptions | null>(null);
        const [reportExportOptionsV2, setReportExportOptionsV2] =
          useState<ReportExportOptionsV2 | null>(null);

        const { fileExportIsPending } = useFileExportContext();

        const onReportExported = () => {
          setReportExportOptions(null);
          setReportExportOptionsV2(null);
        };

        if (!original) {
          return <TableShimmeringDiv />;
        }

        const isOwner =
          (original.report?.isOwner ?? original.reportV2?.isOwner) === true;

        const canUpdate =
          original && (canUpdateReport || (isOwner && canUpdateOwnReports));

        const actions = [
          isOwner && (
            <>
              <DropdownMenu.Item
                key="ShareReport"
                onClick={() =>
                  original.onReportAction &&
                  original.onReportAction(
                    original.report,
                    original.reportV2,
                    GridActionType.Share
                  )
                }
              >
                <Stack alignItems="center" gap="m" width="full">
                  <ShareIcon
                    withHoverEffect
                    fill={IconsFill.textBrand}
                    size={vars.iconSize.m}
                  />
                  <Content id={ContentId.Share} />
                </Stack>
              </DropdownMenu.Item>
              {canUpdate && (
                <DropdownMenu.Item
                  key="EditReport"
                  onClick={() =>
                    original.onReportAction &&
                    original.onReportAction(
                      original.report,
                      original.reportV2,
                      GridActionType.Edit
                    )
                  }
                >
                  <Stack alignItems="center" gap="m" width="full">
                    <EditIcon
                      withHoverEffect
                      fill={IconsFill.textBrand}
                      size={vars.iconSize.m}
                    />
                    <Content id={ContentId.Edit} />
                  </Stack>
                </DropdownMenu.Item>
              )}
            </>
          ),
          original &&
            original.report?.reportIdRedirect == null &&
            (hasExportInventoryFeature ||
              original.report?.groupBy !== ReportGroupBy.ListingId) && (
              <DropdownMenu.Group>
                <DropdownMenu.Label key="DownloadReport">
                  <Stack alignItems="center" gap="m" width="full">
                    <DownloadIcon
                      withHoverEffect
                      fill={IconsFill.textBrand}
                      size={vars.iconSize.m}
                    />
                    <Content id={ContentId.Download} />
                  </Stack>
                </DropdownMenu.Label>
                <DropdownMenu.Item
                  key="DownloadExcel"
                  disabled={Boolean(
                    fileExportIsPending ||
                      reportExportOptions ||
                      reportExportOptionsV2
                  )}
                  onClick={() => {
                    if (original.report) {
                      setReportExportOptions({
                        reportToExport: original.report!,
                        fileType: ReportFileType.XLSX,
                        serverSideExport:
                          original.report!.groupBy !==
                          ReportGroupBy.ArAgingMarketplace,
                      });
                    } else {
                      setReportExportOptionsV2({
                        reportToExport: original.reportV2!,
                        fileType: ReportFileType.XLSX,
                      });
                    }
                  }}
                >
                  <Content id={ContentId.MicrosoftExcelExt} />
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  key="DownloadCsv"
                  disabled={Boolean(
                    fileExportIsPending ||
                      reportExportOptions ||
                      reportExportOptionsV2
                  )}
                  onClick={() => {
                    if (original.report) {
                      setReportExportOptions({
                        reportToExport: original.report!,
                        fileType: ReportFileType.CSV,
                        serverSideExport:
                          original.report!.groupBy !==
                          ReportGroupBy.ArAgingMarketplace,
                      });
                    } else {
                      setReportExportOptionsV2({
                        reportToExport: original.reportV2!,
                        fileType: ReportFileType.CSV,
                      });
                    }
                  }}
                >
                  <Content id={ContentId.CommaSeparatedValuesExt} />
                </DropdownMenu.Item>
                {/*
                We don't support server side PDF export so we hide it for now
                Need to be determined on https://thestubhub.atlassian.net/browse/POS-4291 on how we want to handle this
                <DropdownMenu.Item
                  key="DownloadPdf"
                  onClick={() =>
                    setReportExportOptions({
                      reportToExport: original.report,
                      fileType: ReportFileType.PDF,
                    })
                  }
                >
                  <Content id={ContentId.PdfExt} />
                </DropdownMenu.Item> */}
                <DropdownMenu.Separator />
              </DropdownMenu.Group>
            ),
          canCreateReport &&
            original.report?.reportIdRedirect == null && // not supported for redirected reports yet
            original.report?.groupBy !==
              ReportGroupBy.SaleIdTicketIdCommissionUser &&
            original.reportV2?.reportType !== ReportTypesV2.Commission &&
            original.report?.groupBy !== ReportGroupBy.ArAgingMarketplace && (
              <DropdownMenu.Item
                key="Duplicate"
                onClick={() =>
                  original.onReportAction &&
                  original.onReportAction(
                    original.report,
                    original.reportV2,
                    GridActionType.Duplicate
                  )
                }
              >
                <Stack alignItems="center" gap="m" width="full">
                  <CopyIcon
                    withHoverEffect
                    fill={vars.color.textBrand}
                    size={vars.iconSize.m}
                  />
                  <Content id={ContentId.Duplicate} />
                </Stack>
              </DropdownMenu.Item>
            ),
          isOwner && canUpdate && (
            <DropdownMenu.Item
              key="DeleteReport"
              onClick={() =>
                original.onReportAction &&
                original.onReportAction(
                  original.report,
                  original.reportV2,
                  GridActionType.Delete
                )
              }
            >
              <Stack alignItems="center" gap="m" width="full">
                <DeleteIcon
                  withHoverEffect
                  fill={IconsFill.textBrand}
                  size={vars.iconSize.m}
                />
                <Content id={ContentId.Remove} />
              </Stack>
            </DropdownMenu.Item>
          ),
          canViewHistory &&
            original.report?.globalReportTypeId != null &&
            original.reportV2 == null && // not supported for v2 yet
            original.report?.reportIdRedirect == null && (
              <DropdownMenu.Item
                key="History"
                onClick={() =>
                  original.onReportAction &&
                  original.onReportAction(
                    original.report,
                    original.reportV2,
                    GridActionType.History
                  )
                }
              >
                <Stack alignItems="center" gap="m" width="full">
                  <HistoryIcon
                    withHoverEffect
                    fill={IconsFill.textBrand}
                    size={vars.iconSize.m}
                  />
                  <Content id={ContentId.History} />
                </Stack>
              </DropdownMenu.Item>
            ),
          original.reportV2?.livePagePath && (
            <DropdownMenu.Item
              key="ViewRealTime"
              onClick={() =>
                original.onReportAction &&
                original.onReportAction(
                  original.report,
                  original.reportV2,
                  GridActionType.ViewRealTime
                )
              }
            >
              <Stack alignItems="center" gap="m" width="full">
                <Content id={ContentId.ViewRealTime} />
              </Stack>
            </DropdownMenu.Item>
          ),
        ];

        const visibleActions = actions.filter((a) => !!a);

        return (
          <>
            <TableCellDiv
              align="right"
              style={{ gap: '16px' }}
              showTooltip={false}
            >
              {original.reportV2?.globalReportTypeId != null && (
                <DataLagIndicator
                  lastRefreshTime={original.reportV2.lastRefreshTime}
                />
              )}
              {Boolean(reportExportOptions || reportExportOptionsV2) && (
                <RotatingWrapper>
                  <ProcessingIcon
                    size={vars.iconSize.l}
                    fill={IconsFill.textBrand}
                  />
                </RotatingWrapper>
              )}
              <ButtonWithIcon
                variant="textPlain"
                shape="none"
                disabled={
                  (original.report?.reportId ?? original.reportV2?.reportId) ==
                  null
                }
                icon={<OpenLinkIcon withHoverEffect size={vars.iconSize.m} />}
                onClick={(event: React.MouseEvent) =>
                  original.onReportAction &&
                  original.onReportAction(
                    original.report,
                    original.reportV2,
                    GridActionType.Select,
                    event
                  )
                }
                textContentId={ContentId.OpenReport}
                style={{
                  fontSize: vars.typography.fontSize.xs,
                  width: 'fit-content',
                }}
                className={darkModeLightActive}
                startWithText
              />
              {visibleActions.length > 0 && (
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger
                    asChild
                    disabled={
                      (original.report?.reportId ??
                        original.reportV2?.reportId) == null
                    }
                  >
                    <div
                      style={{
                        padding: `0 ${vars.spacing.sm} 0 ${vars.spacing.xxs}`,
                      }}
                    >
                      <MoreIcon
                        withHoverEffect
                        darkModeLightHover
                        fill={IconsFill.textBrand}
                        align="middle"
                      />
                    </div>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Portal>
                    <DropdownMenu.Content align="end">
                      {...visibleActions}
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              )}
            </TableCellDiv>
            {original.report &&
            original.report.reportType === ReportTypes.Inventory ? (
              <ReportsInventoryExport
                reportExportOptions={reportExportOptions}
                onReportExported={onReportExported}
              />
            ) : (
              <ReportsSaleExport
                reportExportOptions={reportExportOptions}
                onReportExported={onReportExported}
              />
            )}
            {original.reportV2 && (
              <ReportsExportV2
                reportExportOptions={reportExportOptionsV2}
                onReportExported={onReportExported}
              />
            )}
          </>
        );
      },
    },
  ];
